// Where a visitor came from has to be captured on the FIRST page they land on.
// An ad click arrives with fbclid/utm_* in the query string, React Router replaces
// the URL as soon as it resolves a route, and Start Your Journey is several steps
// further down - by the time the registration is posted the parameters are gone.
//
// Mirrors offers-app-client/src/utils/attribution.ts on purpose: both apps report a
// traffic source into the same #bot-lead-follow channel, so the strings have to be
// written the same way to be comparable.

const STORAGE_KEY = "kahunaAttribution"

// Meta rewrites every ad click with fbclid, so that parameter alone is proof of a
// paid click even when the campaign forgot to tag the link. The utm_source values
// cover the links we tag ourselves.
const META_UTM_SOURCES = ["facebook", "fb", "instagram", "ig", "meta"]

// Registration.traffic_source is a CharField(max_length=63) and the value is printed
// verbatim into a Slack line, so keep it short and free of punctuation.
const TRACKED_SOURCE_MAX_LENGTH = 63

export type Attribution = {
  source: string
  medium: string
  campaign: string
  fbclid: string
  landedAt: number
}

const emptyAttribution: Attribution = {
  source: "",
  medium: "",
  campaign: "",
  fbclid: "",
  landedAt: 0
}

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)

const clamp = (value: string): string => value.slice(0, TRACKED_SOURCE_MAX_LENGTH).replace(/_+$/, "")

const read = (): Attribution => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...emptyAttribution }
    return { ...emptyAttribution, ...JSON.parse(raw) }
  } catch {
    // Private mode, corrupted JSON - attribution is never worth breaking a sign-up for.
    return { ...emptyAttribution }
  }
}

// Call as early as possible on every page load. Last touch wins, which is what ad
// platforms report against: a visitor who clicks a second ad is credited to it.
export const captureAttribution = (): void => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)
  const fbclid = params.get("fbclid") || ""
  const utmSource = params.get("utm_source") || ""
  const utmMedium = params.get("utm_medium") || ""
  const utmCampaign = params.get("utm_campaign") || ""

  // Nothing to learn from this load; keep whatever the landing page already stored.
  if (!fbclid && !utmSource && !utmMedium && !utmCampaign) return

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        fbclid,
        landedAt: Date.now()
      })
    )
  } catch {
    // See read(): storage is best effort.
  }
}

export const getAttribution = (): Attribution => read()

export const isMetaTraffic = (attribution: Attribution = read()): boolean =>
  Boolean(attribution.fbclid) || META_UTM_SOURCES.includes(attribution.source.toLowerCase())

// Meta's click identifier cookie format: fb.<subdomainIndex>.<creationTime>.<fbclid>.
// Nothing consumes it yet - it is what the Conversions API will need to match a
// server side CompleteRegistration back to the ad click.
export const getFbc = (): string => {
  const attribution = read()
  if (!attribution.fbclid) return ""
  return `fb.1.${attribution.landedAt || Date.now()}.${attribution.fbclid}`
}

// The measured channel, as opposed to hearing_source which is what the visitor picked
// from the "how did you hear about us" dropdown. The two answer different questions and
// the Slack notification reports both: a visitor who clicked a Meta ad and then selected
// "Instagram" is a different data point from one who typed the URL in.
export const getTrackedSource = (): string => {
  const attribution = read()

  if (isMetaTraffic(attribution)) {
    return clamp(attribution.campaign ? `meta_${slug(attribution.campaign)}` : "meta")
  }

  if (attribution.source) {
    return clamp(
      attribution.campaign ? `${slug(attribution.source)}_${slug(attribution.campaign)}` : slug(attribution.source)
    )
  }

  if (attribution.campaign) {
    return clamp(slug(attribution.campaign))
  }

  return "direct"
}
